import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactTooltip from "react-tooltip";
import { CloudDownload, Info } from "@material-ui/icons";
import moment from "moment";
import { Grid, Typography } from "@material-ui/core";
import {
  SplitTreatments,
  withSplitFactory,
} from "@splitsoftware/splitio-react";
import auth0Client from "../../../../app/auth/Auth";
import sdkConfig from "../../../../infrastructure/split/sdkConfig";
import Table from "../../../../app/components/Table";
import { MonetaryAmount } from "../../../../app/domain/monetary-amount";
import { downloadMovements } from "../../download-movements";
import { translate as t } from "../../../../infrastructure/translate/localization";
import EmptyDataForTableMessage from "../../../../app/components/modals/employee/DeleteEmployeesModal/components/EmptyDataForTableMessage";
import CobeeButton from "../../../../app/components/CobeeButton";
import useStyles from "./styles";
import featureFlagsDictionary from "../../../../infrastructure/split/feature-flags-dictionary";

function MovementsContent({ movements }) {
  const [companyId, setCompanyId] = useState();
  const { container, subTitle, tooltip, tooltipLabel } = useStyles();
  useEffect(() => {
    setCompanyId(auth0Client.getCompanyId());
  }, []);
  const columns = [
    {
      id: "timestamp",
      label: t("common-messages.date"),
    },
    {
      id: "payrollCompany",
      label: t("common-messages.society"),
    },
    {
      id: "costCenter",
      label: t("common-messages.cost-center"),
    },
    {
      id: "concept",
      label: t("my-account-section.concept"),
    },
    {
      id: "payroll",
      label: t("my-account-section.payroll"),
    },
    {
      id: "amount",
      label: t("common-messages.amount"),
      align: "right",
    },
    {
      id: "initial",
      label: t("my-account-section.initial-balance"),
      align: "right",
    },
    {
      id: "final",
      label: t("my-account-section.finish-balance"),
      align: "right",
    },
  ];

  const getAlertInfo = ({ treatments, isReady }) => {
    if (isReady) {
      return (
        <>
          {treatments[featureFlagsDictionary.ET_337].treatment === "on" &&
            Boolean(movements.length) && (
              <div className="alert alert-info mt-3">
                <Info /> {t("my-account-section.consult-old-transactions")}
              </div>
            )}
        </>
      );
    }
    return null;
  };

  return movements.length > 0 ? (
    <Grid container className={container}>
      <Grid container item alignItems="center" justify="space-between">
        <Typography variant="body1" className={subTitle}>
          {t("my-account-section.movements")}
        </Typography>
        <CobeeButton
          styleType="uncontained"
          onClick={() => downloadMovements({ movements })}
        >
          {t("actions.download")}
          <CloudDownload size={16} />
        </CobeeButton>
      </Grid>
      <Table
        textEllipsisLimit={20}
        columns={columns}
        data={movements.map(
          ({
            timestamp,
            payrollCompany,
            costCenter,
            concept,
            payrollMonth,
            operationTimestamp,
            statementTimestamp,
            amount,
            initial,
            final,
            ...rest
          }) => ({
            timestamp: `${moment(timestamp).format("DD/MM/YY")}`,
            payrollCompany: payrollCompany && `${payrollCompany}`,
            costCenter: costCenter && `${costCenter}`,
            concept: (
              <span data-tip data-for={`tooltip-concept-${timestamp}`}>
                {concept.length > 20 ? `${concept.slice(0, 17)}...` : concept}
                <ReactTooltip
                  id={`tooltip-concept-${timestamp}`}
                  className={tooltip}
                  place="top"
                  effect="solid"
                  type="light"
                >
                  <p className={tooltipLabel}>{t("common-messages.concept")}</p>
                  <p>{concept}</p>
                </ReactTooltip>
              </span>
            ),
            payroll: payrollMonth ? (
              <span data-tip data-for={`tooltip-payroll-${timestamp}`}>
                {payrollMonth === "unreported"
                  ? `${t("my-account-section.unreported")}`
                  : `${moment(payrollMonth).format("MMMM")}`}
                <ReactTooltip
                  id={`tooltip-payroll-${timestamp}`}
                  className={tooltip}
                  place="top"
                  effect="solid"
                  type="light"
                >
                  <p className={tooltipLabel}>
                    {t("my-account-section.operation-timestamp")}
                  </p>
                  <p>{moment(operationTimestamp).format("DD/MM/YY")}</p>
                  <p className={tooltipLabel}>
                    {t("my-account-section.statement-timestamp")}
                  </p>
                  <p>{moment(statementTimestamp).format("DD/MM/YY")}</p>
                </ReactTooltip>
              </span>
            ) : (
              "-"
            ),
            amount: new Intl.NumberFormat("es-ES", {
              style: "currency",
              currency: "EUR",
            }).format(MonetaryAmount.ofCurrency(amount).toUnitary()),
            initial: new Intl.NumberFormat("es-ES", {
              style: "currency",
              currency: "EUR",
            }).format(MonetaryAmount.ofCurrency(initial).toUnitary()),
            final: new Intl.NumberFormat("es-ES", {
              style: "currency",
              currency: "EUR",
            }).format(MonetaryAmount.ofCurrency(final).toUnitary()),
            ...rest,
          })
        )}
      />

      <SplitTreatments
        names={[featureFlagsDictionary.ET_337]}
        attributes={{ companyId }}
      >
        {getAlertInfo}
      </SplitTreatments>
    </Grid>
  ) : (
    <EmptyDataForTableMessage message={t("common-messages.empty-movements")} />
  );
}

MovementsContent.defaultProps = {
  movements: [],
};

MovementsContent.propTypes = {
  movements: PropTypes.array,
};

export default withSplitFactory(sdkConfig)(MovementsContent);
